<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-2">
      <h1 class="font-extrabold text-2xl">{{ t("users.title") }}</h1>
      <div class="text-light-gray text-sm">
        {{ t("users.subTitle") }}
      </div>
    </div>
    <div v-if="isLoading" class="flex h-[60vh] justify-center">
      <LoadingIcon :isLoading="isLoading" />
    </div>
    <div class="flex flex-col gap-4" v-if="!isLoading">
      <div class="grid grid-cols-4 gap-3">
        <InfoBox
          :title="t('users.states.totalUsers')"
          :value="userGeneralInformation!.totalUsers"
          :backgroundColor="EventState.events[4].lightColor"
          :textColor="EventState.events[4].color"
        >
          <IconManager
            name="Users"
            :style="{ color: EventState.events[4].color }"
          />
        </InfoBox>

        <InfoBox
          :title="t('users.states.usersWithRoles')"
          :value="userGeneralInformation!.usersWithRoles"
          :backgroundColor="EventState.events[1].lightColor"
          :textColor="EventState.events[1].color"
        >
          <IconManager
            name="ShieldCheck"
            :style="{ color: EventState.events[1].color }"
          />
        </InfoBox>

        <InfoBox
          :title="t('users.states.usersWithoutRoles')"
          :value="userGeneralInformation!.usersWithoutRoles"
          :backgroundColor="EventState.events[2].lightColor"
          :textColor="EventState.events[2].color"
        >
          <IconManager
            name="Warning"
            :style="{ color: EventState.events[2].color }"
          />
        </InfoBox>

        <InfoBox
          :title="t('users.states.availableRoles')"
          :value="userGeneralInformation!.availableRoles"
          :backgroundColor="EventState.events[3].lightColor"
          :textColor="EventState.events[3].color"
        >
          <IconManager
            name="Gear"
            :style="{ color: EventState.events[3].color }"
          />
        </InfoBox>
      </div>

      <div class="flex flex-row gap-2 items-center bg-secondary p-4 rounded-xl">
        <IconManager name="Info" class="text-primary" />
        {{ t("users.info") }}
      </div>

      <FilterBox styleInputOptions="md:grid-cols-3">
        <template #inputOptions>
          <CustomInput
            v-model="filterSearch"
            maxlength="50"
            :title="t('users.filter.search')"
            :placeholder="t('users.filter.search')"
          >
            <IconManager name="Search" class="text-light-gray" />
          </CustomInput>

          <div class="md:col-span-1"></div>
        </template>
        <template #filterButtons>
          <div class="flex flex-row sm:w-[35%] sm:ml-auto gap-2">
            <CustomCleanFilterButton
              class="flex-1"
              title="Limpar filtros"
              @click="clearFilters"
              :numberActivedFilters="getActiveFiltersCount()"
              :hasActiveFilters="hasActiveFilters()"
            />

            <CustomApplyFilterButton
              class="flex-1"
              title="Filtrar"
              @click="applyFilters"
              :hasUnappliedFilters="hasUnappliedFilters()"
            />
          </div>
        </template>
        <template #filterTagBox>
          <AppliedFilterBox :hasActiveFilters="hasActiveFilters()">
            <template #filterTag>
              <FilterTag
                :title="t('users.filter.search')"
                :value="appliedFilters.search"
                :isVisible="filterSearch"
              />
            </template>
          </AppliedFilterBox>
        </template>
      </FilterBox>

      <CustomTable
        :data="users"
        :is-loading="isLoadingTable"
        :total-entries="totalEntries"
        :pagination="{ goToPageCallBack: goToPage }"
        :pageSizeOptions="{ changeCallBack: changePageSize }"
        :columns="[
          { key: 'id', title: t('common.id'), sortable: true },
          { key: 'username', title: t('common.username'), sortable: true },
          {
            key: 'email',
            title: t('common.email'),
            sortable: true,
          },
          {
            key: 'firstName',
            title: t('users.table.firstName'),
            sortable: true,
          },
          {
            key: 'lastName',
            title: t('users.table.lastName'),
            sortable: true,
          },
          {
            key: 'roles',
            title: t('users.table.roles'),
            sortable: true,
            render: (roles: string[]) =>
              h(
                'div',
                { class: 'flex flex-row gap-1 flex-wrap' },
                roles.map((role) =>
                  h(
                    'span',
                    {
                      key: role,
                      class: 'rounded-xl px-2 text-primary bg-secondary',
                    },
                    role,
                  ),
                ),
              ),
          },
          {
            key: 'actions',
            title: t('events.list.table.actions'),
            sortable: false,
            render: (_, row: User) =>
              h(
                'div',
                {
                  class: 'flex flex-row gap-2 items-center ',
                },
                [
                  h(
                    CustomButton,
                    {
                      class: 'bg-secondary! text-primary border-none! p-2!',
                      onClick: () => {
                        selectedUser = row;
                        isDialogOpen = true;
                        selectedRoleNames = row.roles;
                      },
                    },
                    () =>
                      h(IconManager, {
                        name: 'Edit',
                      }),
                  ),
                ],
              ),
          },
        ]"
      />
      <CustomDialog
        v-model="isDialogOpen"
        :title="t('users.dialog.title')"
        :is-submitting="isSubmitting"
        @submit="submitUpdateUserRoles"
        @cancel="isDialogOpen = false"
        :submitText="t('users.dialog.saveProfiles')"
      >
        <div class="flex flex-col gap-3">
          <CustomInput
            :title="t('users.dialog.user')"
            readonly
            :model-value="`${selectedUser?.firstName ?? ''} ${selectedUser?.lastName ?? ''}`"
          />
          <label class="font-bold text-sm">{{
            t("users.dialog.availableProfiles")
          }}</label>
          <div
            class="flex flex-col gap-2"
            v-for="role in availableRoles"
            :key="role.name"
          >
            <div
              class="flex flex-row gap-4 border-b border-b-gray-200 py-1 text-sm"
            >
              <input
                :id="role.name"
                type="checkbox"
                :value="role.name"
                v-model="selectedRoleNames"
              />
              <label :for="role.name" class="cursor-pointer">
                {{ role.name }}
              </label>
            </div>
          </div>
        </div>
      </CustomDialog>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from "@/core/composables/use-toast";
import IconManager from "@/core/IconManager.vue";
import InfoBox from "@/core/components/InfoBox.vue";
import CustomInput from "@/core/components/CustomInput.vue";
import CustomDialog from "@/core/components/CustomDialog.vue";
import CustomButton from "@/core/components/CustomButton.vue";
import CustomTable from "@/core/components/table/CustomTable.vue";
import { useI18n } from "vue-i18n";
import { userRemoteDataSource } from "@/core/models/global-variables";
import { h, onMounted, ref } from "vue";
import { EventState } from "@/modules/events/models/event-state";
import { User } from "../models/user";
import { UserGeneralInformation } from "../models/user-general-information";
import { UserRole } from "../models/user-role";
import LoadingIcon from "@/core/components/LoadingIcon.vue";

//Filter
import CustomCleanFilterButton from "@/core/components/filter/CustomCleanFilterButton.vue";
import CustomApplyFilterButton from "@/core/components/filter/CustomApplyFilterButton.vue";
import AppliedFilterBox from "@/core/components/filter/AppliedFilterBox.vue";
import FilterBox from "@/core/components/filter/FilterBox.vue";
import FilterTag from "@/core/components/filter/FilterTag.vue";

const { t } = useI18n();
const { showError, showSuccess } = useToast();

const users = ref<User[]>([]);

const selectedUser = ref<User | null>(null);
const selectedRoleNames = ref<string[]>([]);

const availableRoles = ref<UserRole[]>([]);
const userGeneralInformation = ref<UserGeneralInformation | null>(null);
const isLoading = ref<boolean>(true);
const isLoadingTable = ref<boolean>(false);

onMounted(() => {
  loadInitialData();
});

const filterSearch = ref<string>("");
const appliedFilters = ref({
  search: "",
});

//TableState
const totalEntries = ref(0);

//Dialog
const isDialogOpen = ref(false);
const isSubmitting = ref(false);

async function submitUpdateUserRoles() {
  isSubmitting.value = true;
  try {
    const response = await userRemoteDataSource.updateUserRoles(
      selectedUser.value!.id,
      { roles: selectedRoleNames.value! },
    );
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    } else showSuccess({ title: t("message.updatedSucess") });

    updateTable();
  } catch (error: any) {
    showError(error.message);
  } finally {
    isSubmitting.value = false;
    isDialogOpen.value = false;
  }
}

async function applyFilters() {
  try {
    isLoadingTable.value = true;
    const response = await userRemoteDataSource.getUsers({
      search: filterSearch.value,
    });
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    appliedFilters.value = {
      search: filterSearch.value,
    };
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoadingTable.value = false;
  }
}

async function clearFilters() {
  isLoadingTable.value = true;

  try {
    appliedFilters.value = {
      search: "",
    };
    filterSearch.value = "";
    await updateTable();
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoadingTable.value = false;
  }
}

function hasActiveFilters(): boolean {
  return appliedFilters.value.search.length > 0;
}

function hasUnappliedFilters(): boolean {
  return appliedFilters.value.search != filterSearch.value;
}

function getActiveFiltersCount(): number {
  return Object.values(appliedFilters.value).filter((value) => value.length > 0)
    .length;
}

async function loadInitialData() {
  isLoading.value = true;

  try {
    const [usersRes, generalInformationRes, availableRolesRes] =
      await Promise.all([
        userRemoteDataSource.getUsers({ pageNumber: 1, pageSize: 10 }),
        userRemoteDataSource.getGeneralInformation(),
        userRemoteDataSource.getUsersRoles(),
      ]);
    if (!usersRes.isSuccess) {
      showError({
        message: usersRes.detail!,
        title: usersRes.title,
        statusCode: usersRes.statusCode,
      });
    }
    totalEntries.value = usersRes.data.totalItems;
    users.value = usersRes.data.items;

    if (!generalInformationRes.isSuccess) {
      showError({
        message: generalInformationRes.detail!,
        title: generalInformationRes.title,
        statusCode: generalInformationRes.statusCode,
      });
    }
    userGeneralInformation.value = generalInformationRes.data;

    if (!availableRolesRes.isSuccess) {
      showError({
        message: availableRolesRes.detail!,
        title: availableRolesRes.title,
        statusCode: availableRolesRes.statusCode,
      });
    }
    availableRoles.value = availableRolesRes.data;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoading.value = false;
  }
}

async function updateTable() {
  isLoadingTable.value = true;

  try {
    const [usersRes, generalInformationRes] = await Promise.all([
      userRemoteDataSource.getUsers({ pageNumber: 1, pageSize: 10 }),
      userRemoteDataSource.getGeneralInformation(),
    ]);
    if (!usersRes.isSuccess) {
      showError({
        message: usersRes.detail!,
        title: usersRes.title,
        statusCode: usersRes.statusCode,
      });
    }
    totalEntries.value = usersRes.data.totalItems;
    users.value = usersRes.data.items;

    if (!generalInformationRes.isSuccess) {
      showError({
        message: generalInformationRes.detail!,
        title: generalInformationRes.title,
        statusCode: generalInformationRes.statusCode,
      });
    }
    userGeneralInformation.value = generalInformationRes.data;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoadingTable.value = false;
  }
}

async function goToPage(page: number, pageSize: number) {
  isLoadingTable.value = true;

  try {
    const response = await userRemoteDataSource.getUsers({
      pageSize: pageSize,
      pageNumber: page,
      search: filterSearch.value,
    });
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    totalEntries.value = response.data.totalItems;
    users.value = response.data.items;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoadingTable.value = false;
  }
}

async function changePageSize(pageSize: number, currentPage: number) {
  isLoadingTable.value = true;

  try {
    const response = await userRemoteDataSource.getUsers({
      pageSize: pageSize,
      pageNumber: currentPage,
      search: filterSearch.value,
    });
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    totalEntries.value = response.data.totalItems;
    users.value = response.data.items;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoadingTable.value = false;
  }
}
</script>
